import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Activity, ArrowRight, ArrowDown, Check, X, ChevronRight,
  KeyRound, Menu,
  Twitter, Linkedin, Github
} from 'lucide-react';
import { landingContent } from '../content/landingContent';
import { Section, SectionHeader, SectionLabel, SectionTitle } from '../components/landing/Section';
import { CTAButton } from '../components/landing/CTAButton';
import { DynamicIcon } from '../components/landing/DynamicIcon';
import ReviewerAccessModal from '../components/ReviewerAccessModal';

export default function LandingPage() {
  const content = landingContent;
  const [accessOpen, setAccessOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Lock body scroll while mobile drawer is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const closeMobile = () => setMobileMenuOpen(false);

  return (
    <div className="min-h-screen bg-[#060608] overflow-x-hidden">
      {/* Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 msml-glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
              <Activity className="w-6 h-6 text-[#2E79FF] transition-transform group-hover:scale-110" />
              <span className="font-semibold text-lg tracking-tight">MSML HEALTH</span>
            </Link>
            <nav className="hidden md:flex items-center gap-8">
              <a href="#how-it-works" className="text-sm text-[#A1A1AA] hover:text-white transition-colors">How It Works</a>
              <a href="#dimensions" className="text-sm text-[#A1A1AA] hover:text-white transition-colors">Dimensions</a>
              <a href="#pricing" className="text-sm text-[#A1A1AA] hover:text-white transition-colors">Pricing</a>
              <button
                onClick={() => setAccessOpen(true)}
                className="text-sm text-[#A1A1AA] hover:text-white transition-colors inline-flex items-center gap-1.5"
                data-testid="nav-reviewer-access"
              >
                <KeyRound className="w-3.5 h-3.5" />
                Reviewer access
              </button>
              <Link to="/login" className="text-sm text-[#A1A1AA] hover:text-white transition-colors">Login</Link>
              <CTAButton href="/signup" size="sm" data-testid="nav-cta">
                {content.hero.primaryCTA.text}
              </CTAButton>
            </nav>
            {/* Mobile: hamburger only — CTA is reachable from inside the drawer and hero */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-md text-white hover:bg-white/5 transition-colors"
              aria-label="Open menu"
              aria-expanded={mobileMenuOpen}
              data-testid="mobile-menu-toggle"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-[60] md:hidden"
          data-testid="mobile-menu-drawer"
          role="dialog"
          aria-modal="true"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={closeMobile}
            data-testid="mobile-menu-backdrop"
          />
          {/* Sheet */}
          <div className="absolute top-0 right-0 h-full w-[88%] max-w-sm bg-[#0B0B10] border-l border-white/10 shadow-2xl flex flex-col">
            <div className="flex items-center justify-between h-16 px-4 border-b border-white/5">
              <div className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-[#2E79FF]" />
                <span className="font-semibold tracking-tight">MSML HEALTH</span>
              </div>
              <button
                type="button"
                onClick={closeMobile}
                className="inline-flex items-center justify-center w-10 h-10 rounded-md text-white hover:bg-white/5 transition-colors"
                aria-label="Close menu"
                data-testid="mobile-menu-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-4 py-6 flex flex-col gap-1">
              <a
                href="#how-it-works"
                onClick={closeMobile}
                className="px-3 py-3 rounded-md text-base text-[#E4E4E7] hover:bg-white/5 hover:text-white transition-colors"
                data-testid="mobile-nav-how-it-works"
              >
                How It Works
              </a>
              <a
                href="#dimensions"
                onClick={closeMobile}
                className="px-3 py-3 rounded-md text-base text-[#E4E4E7] hover:bg-white/5 hover:text-white transition-colors"
                data-testid="mobile-nav-dimensions"
              >
                Dimensions
              </a>
              <a
                href="#pricing"
                onClick={closeMobile}
                className="px-3 py-3 rounded-md text-base text-[#E4E4E7] hover:bg-white/5 hover:text-white transition-colors"
                data-testid="mobile-nav-pricing"
              >
                Pricing
              </a>
              <button
                type="button"
                onClick={() => { closeMobile(); setAccessOpen(true); }}
                className="px-3 py-3 rounded-md text-base text-[#E4E4E7] hover:bg-white/5 hover:text-white transition-colors inline-flex items-center gap-2 text-left"
                data-testid="mobile-nav-reviewer-access"
              >
                <KeyRound className="w-4 h-4" />
                Reviewer access
              </button>
              <Link
                to="/login"
                onClick={closeMobile}
                className="px-3 py-3 rounded-md text-base text-[#E4E4E7] hover:bg-white/5 hover:text-white transition-colors"
                data-testid="mobile-nav-login"
              >
                Login
              </Link>
            </nav>
            <div className="p-4 border-t border-white/5 space-y-2">
              <CTAButton
                href="/signup"
                size="default"
                className="w-full"
                data-testid="mobile-nav-cta"
                onClick={closeMobile}
              >
                {content.hero.primaryCTA.text}
                <ArrowRight className="w-4 h-4 ml-2" />
              </CTAButton>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#2E79FF]/5 via-transparent to-transparent pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#2E79FF]/5 rounded-full blur-[120px] pointer-events-none" />
        
        <div className="relative max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#2E79FF]/10 border border-[#2E79FF]/20 mb-8 animate-fade-in">
            <span className="w-2 h-2 rounded-full bg-[#2E79FF] animate-pulse" />
            <span className="text-xs font-mono uppercase tracking-wider text-[#2E79FF]">
              {content.hero.badge}
            </span>
          </div>
          
          {/* Headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-medium tracking-tighter text-white mb-6 animate-slide-up" data-testid="hero-headline">
            {content.hero.headline}
          </h1>
          
          {/* Subheadline */}
          <p className="text-2xl sm:text-3xl lg:text-4xl font-light text-[#2E79FF] mb-6 animate-slide-up animation-delay-100">
            {content.hero.subheadline}
          </p>
          
          {/* Description */}
          <p className="text-lg sm:text-xl text-[#A1A1AA] max-w-2xl mx-auto mb-10 leading-relaxed animate-slide-up animation-delay-200">
            {content.hero.description}
          </p>
          
          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-slide-up animation-delay-300">
            <CTAButton href={content.hero.primaryCTA.href} size="lg" data-testid="hero-primary-cta">
              {content.hero.primaryCTA.text}
              <ArrowRight className="w-5 h-5 ml-2" />
            </CTAButton>
            <CTAButton href={content.hero.secondaryCTA.href} variant="secondary" size="lg">
              {content.hero.secondaryCTA.text}
              <ArrowDown className="w-5 h-5 ml-2" />
            </CTAButton>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <Section id="how-it-works" dark>
        <SectionHeader
          label={content.howItWorks.label}
          title={content.howItWorks.title}
          subtitle={content.howItWorks.subtitle}
        />
        
        <div className="relative">
          {/* Connection line */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6 lg:gap-4">
            {content.howItWorks.steps.map((step, index) => (
              <div 
                key={step.number}
                className="relative group"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="msml-panel p-6 h-full flex flex-col items-center text-center transition-all duration-300 hover:bg-[#16181D] hover:border-[#2E79FF]/30">
                  {/* Step number */}
                  <div className="w-12 h-12 rounded-full bg-[#2E79FF]/10 border border-[#2E79FF]/20 flex items-center justify-center mb-4 group-hover:bg-[#2E79FF]/20 transition-colors">
                    <span className="text-[#2E79FF] font-mono text-sm">{step.number}</span>
                  </div>
                  
                  {/* Icon */}
                  <div className="mb-4">
                    <DynamicIcon name={step.icon} className="w-6 h-6 text-[#A1A1AA] group-hover:text-[#2E79FF] transition-colors" />
                  </div>
                  
                  <h3 className="text-sm font-medium text-white mb-2">{step.title}</h3>
                  <p className="text-xs text-[#71717A] leading-relaxed">{step.description}</p>
                </div>
                
                {/* Arrow connector */}
                {index < content.howItWorks.steps.length - 1 && (
                  <ChevronRight className="hidden lg:block absolute top-1/2 -right-4 w-8 h-8 text-white/10 -translate-y-1/2" />
                )}
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Six Dimensions */}
      <Section id="dimensions">
        <SectionHeader
          label={content.dimensions.label}
          title={content.dimensions.title}
          subtitle={content.dimensions.subtitle}
        />
        
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {content.dimensions.items.map((dimension, index) => (
            <div 
              key={dimension.name}
              className="msml-panel p-6 group hover:border-l-2 transition-all duration-300"
              style={{ borderLeftColor: dimension.color }}
              data-testid={`dimension-${dimension.name.toLowerCase().replace(/\s/g, '-')}`}
            >
              <div 
                className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 transition-colors"
                style={{ backgroundColor: `${dimension.color}15` }}
              >
                <DynamicIcon 
                  name={dimension.icon} 
                  className="w-6 h-6 transition-colors"
                  style={{ color: dimension.color }}
                />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">{dimension.name}</h3>
              <p className="text-sm text-[#A1A1AA] leading-relaxed">{dimension.description}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Before/After Example */}
      <Section id="example" dark>
        <SectionHeader
          label={content.beforeAfter.label}
          title={content.beforeAfter.title}
          subtitle={content.beforeAfter.subtitle}
        />
        
        <div className="max-w-4xl mx-auto">
          {/* Context */}
          <div className="msml-panel p-4 mb-6 text-center">
            <p className="text-sm text-[#A1A1AA]">
              <span className="text-[#71717A]">User asks:</span>{' '}
              <span className="text-white italic">"{content.beforeAfter.example.context.split('"')[1]}"</span>
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Before */}
            <div className="msml-panel p-6 border-l-2 border-[#EF4444]" data-testid="before-example">
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-mono uppercase tracking-wider text-[#EF4444]">
                  {content.beforeAfter.example.before.label}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-light text-[#EF4444]">
                    {content.beforeAfter.example.before.score}
                  </span>
                  <span className="text-xs text-[#71717A]">HQ Score</span>
                </div>
              </div>
              
              <p className="text-sm text-[#A1A1AA] leading-relaxed mb-6 max-h-48 overflow-y-auto scrollbar-thin">
                {content.beforeAfter.example.before.text}
              </p>
              
              <div className="border-t border-white/10 pt-4">
                <p className="text-xs font-medium text-[#EF4444] mb-2">Issues Identified</p>
                <ul className="space-y-1">
                  {content.beforeAfter.example.before.issues.map((issue, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-xs text-[#A1A1AA]">
                      <X className="w-3 h-3 text-[#EF4444]" />
                      {issue}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            
            {/* After */}
            <div className="msml-panel p-6 border-l-2 border-[#10B981] relative overflow-hidden" data-testid="after-example">
              {/* Glow effect */}
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-[#10B981]/10 rounded-full blur-3xl" />
              
              <div className="relative">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-mono uppercase tracking-wider text-[#10B981]">
                    {content.beforeAfter.example.after.label}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-light text-[#10B981]">
                      {content.beforeAfter.example.after.score}
                    </span>
                    <span className="text-xs text-[#71717A]">HQ Score</span>
                  </div>
                </div>
                
                <p className="text-sm text-white leading-relaxed mb-6 whitespace-pre-line">
                  {content.beforeAfter.example.after.text}
                </p>
                
                <div className="border-t border-white/10 pt-4">
                  <p className="text-xs font-medium text-[#10B981] mb-2">Improvements Made</p>
                  <ul className="space-y-1">
                    {content.beforeAfter.example.after.improvements.map((improvement, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-xs text-[#A1A1AA]">
                        <Check className="w-3 h-3 text-[#10B981]" />
                        {improvement}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          {/* Score improvement indicator */}
          <div className="flex items-center justify-center mt-8">
            <div className="msml-panel px-6 py-3 flex items-center gap-4">
              <span className="text-sm text-[#71717A]">Score improvement:</span>
              <span className="text-2xl font-semibold text-[#10B981]">
                +{content.beforeAfter.example.after.score - content.beforeAfter.example.before.score}
              </span>
              <span className="text-sm text-[#10B981]">points</span>
            </div>
          </div>
        </div>
      </Section>

      {/* Why This Matters Now */}
      <Section id="why-now">
        <SectionHeader
          label={content.whyNow.label}
          title={content.whyNow.title}
          subtitle={content.whyNow.subtitle}
        />
        
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {content.whyNow.points.map((point, index) => (
            <div key={index} className="msml-panel p-6 text-center group hover:border-[#2E79FF]/30 transition-colors">
              <div className="text-4xl sm:text-5xl font-light text-[#2E79FF] mb-3">
                {point.title}
              </div>
              <p className="text-sm text-[#A1A1AA]">{point.description}</p>
            </div>
          ))}
        </div>
        
        <div className="msml-panel p-8 text-center border-[#2E79FF]/30">
          <p className="text-lg text-white leading-relaxed max-w-3xl mx-auto">
            {content.whyNow.statement}
          </p>
        </div>
      </Section>

      {/* Privacy First */}
      <Section id="privacy" dark>
        <SectionHeader
          label={content.privacy.label}
          title={content.privacy.title}
          subtitle={content.privacy.subtitle}
        />
        
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {content.privacy.features.map((feature, index) => (
            <div key={index} className="msml-panel p-6 group hover:bg-[#16181D] transition-colors">
              <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center mb-4 group-hover:bg-[#2E79FF]/10 transition-colors">
                <DynamicIcon 
                  name={feature.icon} 
                  className="w-5 h-5 text-[#A1A1AA] group-hover:text-[#2E79FF] transition-colors" 
                />
              </div>
              <h3 className="text-sm font-medium text-white mb-2">{feature.title}</h3>
              <p className="text-xs text-[#71717A] leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Use Cases */}
      <Section id="use-cases">
        <SectionHeader
          label={content.useCases.label}
          title={content.useCases.title}
          subtitle={content.useCases.subtitle}
        />
        
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {content.useCases.cases.map((useCase, index) => (
            <div 
              key={index} 
              className="msml-panel p-6 group hover:border-[#2E79FF]/30 hover:bg-[#0F1014] transition-all"
            >
              <div className="w-10 h-10 rounded-lg bg-[#2E79FF]/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <DynamicIcon name={useCase.icon} className="w-5 h-5 text-[#2E79FF]" />
              </div>
              <h3 className="text-base font-medium text-white mb-2">{useCase.title}</h3>
              <p className="text-sm text-[#A1A1AA] leading-relaxed">{useCase.description}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Pricing */}
      <Section id="pricing" dark>
        <SectionHeader
          label={content.pricing.label}
          title={content.pricing.title}
          subtitle={content.pricing.subtitle}
        />
        
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {content.pricing.plans.map((plan, index) => (
            <div 
              key={index}
              className={`msml-panel p-6 relative flex flex-col ${
                plan.highlighted ? 'border-[#2E79FF] bg-[#2E79FF]/5' : ''
              }`}
              data-testid={`pricing-${plan.name.toLowerCase()}`}
            >
              {plan.highlighted && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="px-3 py-1 bg-[#2E79FF] text-white text-xs font-medium rounded-full">
                    Most Popular
                  </span>
                </div>
              )}
              
              <div className="mb-6">
                <h3 className="text-lg font-medium text-white mb-1">{plan.name}</h3>
                <p className="text-sm text-[#71717A]">{plan.description}</p>
              </div>
              
              <div className="mb-6">
                <span className="text-4xl font-light text-white">{plan.price}</span>
                {plan.period && <span className="text-[#71717A]">{plan.period}</span>}
              </div>
              
              <ul className="space-y-3 mb-8 flex-grow">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                    <Check className="w-4 h-4 text-[#10B981]" />
                    {feature}
                  </li>
                ))}
              </ul>
              
              <CTAButton 
                href={plan.cta.href}
                variant={plan.highlighted ? 'primary' : 'secondary'}
                className="w-full"
              >
                {plan.cta.text}
              </CTAButton>
            </div>
          ))}
        </div>
      </Section>

      {/* CTA Footer */}
      <Section className="border-t border-white/10">
        <div className="text-center mb-16">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-white mb-4">
            {content.footer.cta.headline}
          </h2>
          <p className="text-lg text-[#A1A1AA] mb-8">
            {content.footer.cta.subheadline}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <CTAButton href={content.footer.cta.primaryCTA.href} size="lg" data-testid="footer-primary-cta">
              {content.footer.cta.primaryCTA.text}
              <ArrowRight className="w-5 h-5 ml-2" />
            </CTAButton>
            <CTAButton href={content.footer.cta.secondaryCTA.href} variant="secondary" size="lg">
              {content.footer.cta.secondaryCTA.text}
            </CTAButton>
          </div>
        </div>
      </Section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-8 mb-12">
            {/* Brand */}
            <div className="lg:col-span-2">
              <Link to="/" className="flex items-center gap-2 mb-4">
                <Activity className="w-6 h-6 text-[#2E79FF]" />
                <span className="font-semibold text-lg">MSML HEALTH</span>
              </Link>
              <p className="text-sm text-[#71717A] max-w-xs">
                Measuring what AI does to humans. Privacy-first human impact intelligence.
              </p>
            </div>
            
            {/* Product Links */}
            <div>
              <h4 className="text-xs font-medium text-white uppercase tracking-wider mb-4">Product</h4>
              <ul className="space-y-2">
                {content.footer.links.product.map((link, idx) => (
                  <li key={idx}>
                    <a href={link.href} className="text-sm text-[#71717A] hover:text-white transition-colors">
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Company Links */}
            <div>
              <h4 className="text-xs font-medium text-white uppercase tracking-wider mb-4">Company</h4>
              <ul className="space-y-2">
                {content.footer.links.company.map((link, idx) => (
                  <li key={idx}>
                    <a href={link.href} className="text-sm text-[#71717A] hover:text-white transition-colors">
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Legal Links */}
            <div>
              <h4 className="text-xs font-medium text-white uppercase tracking-wider mb-4">Legal</h4>
              <ul className="space-y-2">
                {content.footer.links.legal.map((link, idx) => (
                  <li key={idx}>
                    <a href={link.href} className="text-sm text-[#71717A] hover:text-white transition-colors">
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          {/* Bottom bar */}
          <div className="flex flex-col sm:flex-row items-center justify-between pt-8 border-t border-white/10">
            <p className="text-sm text-[#71717A]">{content.footer.copyright}</p>
            <div className="flex items-center gap-4 mt-4 sm:mt-0">
              <a href="https://twitter.com" className="text-[#71717A] hover:text-white transition-colors">
                <Twitter className="w-5 h-5" />
              </a>
              <a href="https://linkedin.com" className="text-[#71717A] hover:text-white transition-colors">
                <Linkedin className="w-5 h-5" />
              </a>
              <a href="https://github.com" className="text-[#71717A] hover:text-white transition-colors">
                <Github className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* CSS Animations */}
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slide-up {
          from { 
            opacity: 0;
            transform: translateY(20px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 0.6s ease-out forwards;
        }
        
        .animate-slide-up {
          animation: slide-up 0.6s ease-out forwards;
        }
        
        .animation-delay-100 {
          animation-delay: 100ms;
        }
        
        .animation-delay-200 {
          animation-delay: 200ms;
        }
        
        .animation-delay-300 {
          animation-delay: 300ms;
        }
        
        .animation-delay-400 {
          animation-delay: 400ms;
        }
      `}</style>

      {/* Phase 4 Reviewer Access Modal */}
      <ReviewerAccessModal open={accessOpen} onClose={() => setAccessOpen(false)} />
    </div>
  );
}
